import { useClient } from '@/context/ClientContext'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import {
  CreateShareLinkResponse,
  ShareLink,
  ShareLinkExpiryHours,
} from '@droidmap/asset-service-contract'
import { DroidMapMetadata } from '@droidmap/shared-service-contract'

/** Matches the server default; small so the share dialog stays compact. */
export const SHARE_LINKS_PAGE_SIZE = 5

export type UseShareLinksResult = {
  shareLinks: ShareLink[]
  isLoading: boolean
  /** Current 0-indexed page. */
  page: number
  setPage: (page: number) => void
  /** Total links across all pages (for the pager + header count). */
  totalCount: number
  /** Number of pages (>= 1). */
  pageCount: number
  createShareLink: (
    expiresInHours: ShareLinkExpiryHours,
  ) => Promise<CreateShareLinkResponse>
  isCreating: boolean
  revokeShareLink: (shareLinkId: string) => Promise<void>
  isRevoking: boolean
  error: Error | null
}

/**
 * Authenticated share-link management for one asset set: paginated list,
 * create, revoke. The raw URL token only exists in `createShareLink`'s
 * resolved value — it is returned by the backend exactly once and never
 * appears in the list query.
 *
 * The list is offset-paged (a heavily-shared set can have many links).
 * Mutations invalidate the whole `['shareLinks', assetSetId]` key so every
 * cached page refetches; create also snaps back to page 0 so the new link
 * (newest first) is visible.
 */
export function useShareLinks(
  assetSetId: string,
  metadata?: DroidMapMetadata,
): UseShareLinksResult {
  const { assetClient } = useClient()
  const queryClient = useQueryClient()
  const listKey = ['shareLinks', assetSetId]
  const [page, setPage] = useState(0)

  const listQuery = useQuery({
    queryKey: [...listKey, page],
    queryFn: async () =>
      assetClient.listShareLinks(
        { assetSetId, page, pageSize: SHARE_LINKS_PAGE_SIZE },
        metadata,
      ),
    // Keep the current page visible while the next page loads — no flash of
    // empty list when paging.
    placeholderData: keepPreviousData,
  })

  const createMutation = useMutation({
    mutationFn: async (expiresInHours: ShareLinkExpiryHours) =>
      assetClient.createShareLink({ assetSetId, expiresInHours }, metadata),
    onSuccess: () => {
      setPage(0)
      queryClient.invalidateQueries({ queryKey: listKey })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: async (shareLinkId: string) => {
      await assetClient.revokeShareLink({ assetSetId, shareLinkId }, metadata)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listKey })
    },
  })

  const totalCount = listQuery.data?.totalCount ?? 0
  const pageCount = Math.max(1, Math.ceil(totalCount / SHARE_LINKS_PAGE_SIZE))

  // Clamp the page if the last item on the last page was just revoked/removed
  // (totalCount shrank), so we never sit on an empty trailing page.
  const clampedSetPage = useCallback(
    (next: number) => setPage(Math.max(0, next)),
    [],
  )

  return {
    shareLinks: listQuery.data?.items ?? [],
    isLoading: listQuery.isLoading,
    page: listQuery.data?.page ?? page,
    setPage: clampedSetPage,
    totalCount,
    pageCount,
    createShareLink: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    revokeShareLink: revokeMutation.mutateAsync,
    isRevoking: revokeMutation.isPending,
    error:
      (listQuery.error as Error) ||
      (createMutation.error as Error) ||
      (revokeMutation.error as Error) ||
      null,
  }
}
